/**
 * ═══════════════════════════════════════════════════════════════
 * SINGLE SOURCE OF TRUTH — Aditya Labs Pricing & Plan Configuration
 * ═══════════════════════════════════════════════════════════════
 *
 * ALL plan names, prices, limits, and features are defined HERE.
 * Every other file in the codebase MUST import from this file.
 *
 * DO NOT hardcode plan prices, limits, or feature lists elsewhere.
 * If you need new plan data, add it HERE first.
 *
 * DB note: Supabase stores "pro" for the Growth plan (CHECK constraint).
 * Use toDbPlan() / fromDbPlan() for conversions.
 * ═══════════════════════════════════════════════════════════════
 */

// ── Plan tier identifiers (canonical code names) ─────────────
export type PlanTier = "free" | "growth" | "business" | "agency" | "agency_elite" | "enterprise";

// DB stores "pro" instead of "growth" due to CHECK constraint
export type DbPlanTier = "free" | "pro" | "business" | "agency" | "agency_elite" | "enterprise";

export const PLAN_CURRENCY_CODE = "USD";
export const PLAN_CURRENCY_SYMBOL = "$";
export const PLAN_TRIAL_DAYS = 14;
export const PLAN_TRIAL_TIERS: PlanTier[] = ["growth"];

// ── Add-on / overage display prices ──────────────────────────
// Canonical values for the small "Need more?" overage badges and FAQ
// answers rendered on /pricing. They are NOT plan prices — they are
// supplementary capacity prices a customer can stack on top of their
// plan. Pinned here so the pricing-integrity Playwright suite can
// validate them as canonical (not "stale / off-config") amounts
// without hard-coding magic numbers in the test.
//
// If you change these values you MUST also update the matching i18n
// strings (e.g. pricing.extraMessages and pricing.extraAgents in
// src/messages/*.json) and the FAQ answer (pricing.faq5A) — the
// rendered copy is not interpolated from these constants today, so
// the strings have to be kept in lock-step by hand.
export const EXTRA_MESSAGE_PRICE_PER_1000 = 10; // $10 per 1,000 extra messages
export const EXTRA_AGENT_PACK_PRICE_PER_MONTH = 29; // $29/month per 5 extra agents

export function formatPlanAmount(amount: number): string {
  const hasCents = !Number.isInteger(amount);
  return `${PLAN_CURRENCY_SYMBOL}${amount.toLocaleString("en-US", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

// ── Prices ───────────────────────────────────────────────────
export interface PlanPrice {
  monthly: number;       // Full monthly price
  annualMonthly: number; // Per-month price when billed annually
  annualTotal: number;   // Total annual price
}

export const PLAN_PRICES: Record<PlanTier, PlanPrice> = {
  free:          { monthly: 0,   annualMonthly: 0,   annualTotal: 0 },
  growth:        { monthly: 49,  annualMonthly: 39,  annualTotal: 468 },
  business:      { monthly: 99,  annualMonthly: 79,  annualTotal: 948 },
  agency:        { monthly: 297, annualMonthly: 267, annualTotal: 3204 },
  agency_elite:  { monthly: 497, annualMonthly: 449, annualTotal: 5388 },
  enterprise:    { monthly: 999, annualMonthly: 899, annualTotal: 10788 },
};

// ── Limits ───────────────────────────────────────────────────
export interface PlanLimits {
  agents: number;
  messages: number;
  teamMembers: number;
  subAccounts: number;
}

export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free:          { agents: 1,  messages: 50,      teamMembers: 1,   subAccounts: 0 },
  growth:        { agents: 5,  messages: 5_000,   teamMembers: 3,   subAccounts: 0 },
  business:      { agents: 10, messages: 25_000,  teamMembers: 10,  subAccounts: 0 },
  agency:        { agents: 15, messages: 75_000,  teamMembers: 25,  subAccounts: 15 },
  agency_elite:  { agents: 15, messages: 200_000, teamMembers: 100, subAccounts: 15 },
  enterprise:    { agents: 50, messages: 500_000, teamMembers: 500, subAccounts: 100 },
};

// ── API Rate Limits (requests per minute) ────────────────────
export const PLAN_RATE_LIMITS: Record<PlanTier, number> = {
  free:          10,
  growth:        30,
  business:      60,
  agency:        120,
  agency_elite:  300,
  enterprise:    600,
};

/** Get rate limit for a plan (handles DB "pro" alias) */
export function getRateLimitForPlan(plan: string): number {
  return PLAN_RATE_LIMITS[normalizePlan(plan)] || PLAN_RATE_LIMITS.free;
}

// ── Features per plan ────────────────────────────────────────
export const PLAN_FEATURES: Record<PlanTier, string[]> = {
  free: [
    "1 AI agent",
    "50 messages/month",
    "Web Widget",
    "Basic analytics",
  ],
  growth: [
    "5 AI agents",
    "5,000 messages/month",
    "Knowledge Base",
    "Advanced analytics",
    "Guided industry setup",
    "Custom branding",
  ],
  business: [
    "10 AI agents",
    "25,000 messages/month",
    "Web + Email channels",
    "Knowledge Base",
    "Custom branding",
    "API access",
    "Team collaboration",
    "Advanced analytics",
    "Extended thinking (up to 10K tokens)",
    "Priority support",
  ],
  agency: [
    "15 sub-accounts",
    "75,000 messages/month",
    "Widget white-label",
    "Knowledge Base + RAG",
    "API + Webhooks",
    "Web + Email channels",
    "Priority support",
    "Custom integrations",
    "Team collaboration",
  ],
  agency_elite: [
    "15 active sub-accounts",
    "200,000 messages/month",
    "Full white-label dashboard",
    "Web + Email channels",
    "API + Webhooks",
    "+10 accounts for $197/mo",
    "Knowledge Base + RAG",
    "Priority support",
    "Custom integrations",
  ],
  enterprise: [
    "50 AI agents across offices",
    "500,000 messages/month",
    "99.9% uptime target",
    "Dedicated account manager",
    "Custom domain + full white-label",
    "Built on SOC 2 Type II certified providers (Vercel, Supabase)",
    "Priority support and guided onboarding",
    "Web + Email + API + Voice channels",
    "Knowledge Base + RAG + PDF upload",
    "Full custom integrations",
    "Extended thinking (expanded budget)",
    "Sandbox execution (isolated agent VMs)",
  ],
};

// ── Channels per plan ────────────────────────────────────────
export const PLAN_CHANNELS: Record<PlanTier, string> = {
  free:          "Web Widget",
  growth:        "Web Widget",
  business:      "Web + Email",
  agency:        "Web + Email",
  agency_elite:  "Web + Email",
  enterprise:    "Web + Email + API + Voice",
};

// ── Display names ────────────────────────────────────────────
export const PLAN_DISPLAY_NAMES: Record<string, string> = {
  free: "Free",
  pro: "Growth",      // DB alias
  growth: "Growth",
  business: "Business",
  agency: "Agency",
  agency_elite: "Agency Elite",
  enterprise: "Enterprise",
};

// ── Annual savings ───────────────────────────────────────────
export function getAnnualSavings(plan: PlanTier): number {
  const p = PLAN_PRICES[plan];
  return (p.monthly * 12) - p.annualTotal;
}

// ── Overage Billing ─────────────────────────────────────────
// When a plan exceeds its message limit, charge per-message overage
// This prevents enterprise plans from losing money at scale
export interface OverageConfig {
  enabled: boolean;
  perMessageCents: number; // Cost in cents per overage message
  gracePeriodPercent: number; // % over limit before overage kicks in (e.g., 10 = 10% grace)
  hardCapMultiplier: number; // Maximum overage as multiplier of plan limit (e.g., 2 = 2x limit)
}

export const PLAN_OVERAGE: Record<PlanTier, OverageConfig> = {
  free:          { enabled: false, perMessageCents: 0,   gracePeriodPercent: 0,  hardCapMultiplier: 1 },
  growth:        { enabled: true,  perMessageCents: 0.5, gracePeriodPercent: 10, hardCapMultiplier: 2 },
  business:      { enabled: true,  perMessageCents: 0.3, gracePeriodPercent: 10, hardCapMultiplier: 3 },
  agency:        { enabled: true,  perMessageCents: 0.2, gracePeriodPercent: 10, hardCapMultiplier: 3 },
  agency_elite:  { enabled: true,  perMessageCents: 0.2, gracePeriodPercent: 10, hardCapMultiplier: 3 },
  enterprise:    { enabled: true,  perMessageCents: 0.2, gracePeriodPercent: 5,  hardCapMultiplier: 5 },
};

/** Calculate overage cost for a plan given message count */
export function calculateOverage(plan: PlanTier, messageCount: number): { overageMessages: number; overageCents: number } {
  const limits = PLAN_LIMITS[plan];
  const overage = PLAN_OVERAGE[plan];
  if (!overage.enabled || messageCount <= limits.messages) {
    return { overageMessages: 0, overageCents: 0 };
  }
  const graceLimit = Math.floor(limits.messages * (1 + overage.gracePeriodPercent / 100));
  if (messageCount <= graceLimit) {
    return { overageMessages: 0, overageCents: 0 };
  }
  const hardCap = limits.messages * overage.hardCapMultiplier;
  const billableMessages = Math.min(messageCount, hardCap) - graceLimit;
  return {
    overageMessages: Math.max(0, billableMessages),
    overageCents: Math.max(0, billableMessages) * overage.perMessageCents,
  };
}

// ── Plan ordering (for upgrade paths) ────────────────────────
export const PLAN_ORDER: PlanTier[] = [
  "free", "growth", "business", "agency", "agency_elite", "enterprise",
];

export function isUpgrade(from: string, to: string): boolean {
  const fromIdx = PLAN_ORDER.indexOf(normalizePlan(from) as PlanTier);
  const toIdx = PLAN_ORDER.indexOf(normalizePlan(to) as PlanTier);
  return toIdx > fromIdx;
}

// ── DB ↔ Code conversions ────────────────────────────────────
/** Normalize any plan name to canonical code name */
export function normalizePlan(plan: string): PlanTier {
  if (plan === "pro" || plan === "starter") return "growth";
  if (plan === "professional") return "business";
  if (PLAN_ORDER.includes(plan as PlanTier)) return plan as PlanTier;
  return "free";
}

/** Convert code plan name to DB-safe value */
export function toDbPlan(plan: string): DbPlanTier {
  if (plan === "growth") return "pro";
  return plan as DbPlanTier;
}

/** Convert DB plan_tier to canonical code name */
export function fromDbPlan(dbPlan: string): PlanTier {
  if (dbPlan === "pro") return "growth";
  return dbPlan as PlanTier;
}

/** Get display-friendly plan name from any input */
export function displayPlan(plan: string): string {
  return PLAN_DISPLAY_NAMES[plan] || plan.charAt(0).toUpperCase() + plan.slice(1);
}

// ── Feature comparison table data ────────────────────────────
export interface FeatureComparison {
  agents: string;
  messages: string;
  channels: string;
  customTraining: string;
  analytics: string;
  support: string;
  branding: string;
  api: boolean;
  customIntegrations: boolean;
  teamCollaboration: boolean;
  sla: string;
  compliance: string;
  security: string;
}

export const PLAN_COMPARISON: Record<PlanTier, FeatureComparison> = {
  free: {
    agents: "1", messages: "50/month", channels: "Web Widget",
    customTraining: "---", analytics: "Basic", support: "Community",
    branding: "---", api: false, customIntegrations: false,
    teamCollaboration: false, sla: "Best effort", compliance: "Privacy-oriented review", security: "---",
  },
  growth: {
    agents: "5", messages: "5,000/month", channels: "Web Widget",
    customTraining: "Basic KB", analytics: "Advanced", support: "Community",
    branding: "Custom branding", api: false, customIntegrations: false,
    teamCollaboration: false, sla: "Standard", compliance: "Privacy-oriented review", security: "---",
  },
  business: {
    agents: "10", messages: "25,000/month", channels: "Web + Email",
    customTraining: "Basic KB", analytics: "Advanced + Honest Metrics", support: "Standard",
    branding: "Custom branding", api: true, customIntegrations: false,
    teamCollaboration: true, sla: "Priority", compliance: "Privacy-oriented review + Fact-check workflow", security: "Basic DLP scanning",
  },
  agency: {
    agents: "15 sub-accounts", messages: "75,000/month", channels: "Web + Email",
    customTraining: "KB + RAG", analytics: "Advanced + Custom + Honest Metrics", support: "Priority",
    branding: "Widget white-label", api: true, customIntegrations: true,
    teamCollaboration: true, sla: "Priority", compliance: "Privacy-oriented review", security: "Zero-Trust + DLP + Audit Ledger",
  },
  agency_elite: {
    agents: "15 sub-accounts (expandable)", messages: "200,000/month", channels: "Web + Email",
    customTraining: "KB + RAG", analytics: "Advanced + Custom", support: "Priority",
    branding: "Full white-label dashboard", api: true, customIntegrations: true,
    teamCollaboration: true, sla: "Priority", compliance: "Privacy-oriented review", security: "DLP + Audit Ledger + RLS",
  },
  enterprise: {
    agents: "50 (multi-office)", messages: "500,000/month", channels: "Web + Email + API + Voice",
    customTraining: "KB + RAG + PDF upload", analytics: "Advanced + Custom + Honest Metrics",
    support: "Dedicated AM (4h response target)", branding: "Full white-label + custom domain",
    api: true, customIntegrations: true, teamCollaboration: true,
    sla: "99.9% uptime target", compliance: "Privacy-oriented review", security: "Built on SOC 2 Type II certified providers (Vercel, Supabase)",
  },
};

// ── Helpers for API routes ───────────────────────────────────
/** Get limits for a plan (handles DB "pro" alias) */
export function getLimitsForPlan(plan: string): PlanLimits {
  return PLAN_LIMITS[normalizePlan(plan)] || PLAN_LIMITS.free;
}

/** Get prices for a plan (handles DB "pro" alias) */
export function getPricesForPlan(plan: string): PlanPrice {
  return PLAN_PRICES[normalizePlan(plan)] || PLAN_PRICES.free;
}

/** Get features for a plan (handles DB "pro" alias) */
export function getFeaturesForPlan(plan: string): string[] {
  return PLAN_FEATURES[normalizePlan(plan)] || PLAN_FEATURES.free;
}

/** Get available upgrades from current plan */
export function getAvailableUpgrades(currentPlan: string) {
  const current = normalizePlan(currentPlan);
  const currentIdx = PLAN_ORDER.indexOf(current);

  return PLAN_ORDER
    .filter((_, i) => i > currentIdx && i < PLAN_ORDER.length - 1) // exclude enterprise (Contact Sales)
    .map(plan => ({
      plan,
      displayName: PLAN_DISPLAY_NAMES[plan],
      ...PLAN_PRICES[plan],
      limits: PLAN_LIMITS[plan],
      features: PLAN_FEATURES[plan],
    }));
}
